"use client"

import { useState, useEffect, useRef } from "react"
import { Backdrop, CircularProgress, Button } from "@mui/material"
import { useNavigate, Link} from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { InputText } from "primereact/inputtext"
import { FilterMatchMode } from "primereact/api"
import { Dropdown } from "primereact/dropdown"
import { InputNumber } from "primereact/inputnumber"
import "./table.css"
import Select from "react-select"
import { MdRefresh } from "react-icons/md"
import { TabView, TabPanel } from "primereact/tabview"
import { MdNotificationsActive } from "react-icons/md"
import Highcharts from "highcharts"
import HighchartsMore from "highcharts/highcharts-more"
import SolidGauge from "highcharts/modules/solid-gauge"
import HighchartsReact from "highcharts-react-official"
import Treemap from "highcharts/modules/treemap"
import Annotations from "highcharts/modules/annotations"

// Initialize the modules
HighchartsMore(Highcharts)
SolidGauge(Highcharts)
Treemap(Highcharts)
Annotations(Highcharts)

const AUTH_KEYS = [
  "JWT", "userName", "organization", "loginType", "companyid", "companyName", "features",
  "legal1", "business1", "country1", "site1", "year1",
  "scope2legal", "scope2business", "scope2country", "scope2site", "scope2supplier", "scope2year",
  "businessTravelLegal", "businessTravelBusiness", "businessTravelCountry", "businessTravelSite", "businessTravelYear",
  "transportlegal", "transportbusiness", "transportcountry", "transportsite", "transportyear",
  "sbusiness", "scountry", "sjob", "slegal", "ssite", "syear",
  "allconfig", "report", "reportStatusData", "report_name",
  "savedReports", "savedStatus", "savedSurveys",
  "brsr_report_id", "brsr_sectionA_data", "brsrSectionCPopup",
  "step1", "step2", "step2Draft", "step3", "step4",
  "answers", "answers1", "answers2", "tableAnswers",
  "selectedOptions", "csrd", "type", "mdrp", "e1", "s1",
  "scfFormInProgress", "scfStep1", "scfStep2",
  "editFieldMapping", "popup1", "popup2", "popup3",
  "bloburl", "logo", "hash", "linkToken", "linkUrl",
  "weblink", "statusDashboardUrl", "statusReportFile",
  "oldp", "redirectAfterLogin",
]

const ESGBenchmarking = () => {
  const navigate = useNavigate()
  const { logout } = useAuth0()
  const [isOpen, setIsOpen] = useState(false)
  const username = localStorage.getItem("userName")
  const handleLogout = () => {
    AUTH_KEYS.forEach(k => localStorage.removeItem(k))
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    companyName: "",
    sector: "",
    country: [],
    competitors: [],
    year: "",
  })
  const [viewMode, setViewMode] = useState("table")
  const [data, setData] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [sectorOptions, setSectorOptions] = useState([])
  const [competitorsOptions, setCompetitorsOptions] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [yearOptions, setYearOptions] = useState([])
  const [nextToken, setNextToken] = useState(null)
  const [tab3total, setTab3Total] = useState()
  const [tab2total, setTab2Total] = useState()
  const [searchText, setSearchText] = useState("")
  const [fontSize, setFontSize] = useState(window.innerWidth >= 1400 ? "15px" : "13px")
  const companyid = localStorage.getItem("companyid");
  const [kpiData, setKpiData] = useState({
    company_count: 0,
    average_csrd_page_count: 0,
    average_material_topics_count: 0
  })
  const [chartData, setChartData] = useState({
    external_assurance_percent: 0,
    eu_taxonomy_percent: 0,
    companies_by_country: [],
    emissions_profile: [],
    workforce_and_board_diversity: [],
    iro_visual_format: {},
    esrs_standards_mentioned: [],
    framework_alignment: []
  });

  const resetSelections = () => {
    setFormData({
      companyName: null,
      sector: [],
      country: [],
      competitors: [],
      year: "",
    })
    setSearchText("")
    setFilteredEmissionsData([])
    setFilteredTargetsData([])
    setFilteredReportingData([])
  }

  const [filteredEmissionsData, setFilteredEmissionsData] = useState([])
  const [filteredTargetsData, setFilteredTargetsData] = useState([])
  const [filteredReportingData, setFilteredReportingData] = useState([])

  const fileInputRef = useRef(null)

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (event) => {
    const files = event.target.files
    if (files.length > 0) {
      console.log("Files selected:", files)
    }
  }

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    company: { value: null, matchMode: FilterMatchMode.CONTAINS },
    sector: { value: null, matchMode: FilterMatchMode.CONTAINS },
    country: { value: null, matchMode: FilterMatchMode.CONTAINS },
    year: { value: null, matchMode: FilterMatchMode.CONTAINS },
    revenue: { value: null, matchMode: FilterMatchMode.CONTAINS },
    employees: { value: null, matchMode: FilterMatchMode.CONTAINS },
    scope1: { value: null, matchMode: FilterMatchMode.CONTAINS },
    scope2: { value: null, matchMode: FilterMatchMode.CONTAINS },
    scope3: { value: null, matchMode: FilterMatchMode.CONTAINS },
    totalEmissions: { value: null, matchMode: FilterMatchMode.CONTAINS },
    economicEmissionIntensity: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
  })

  const [globalFilterValue, setGlobalFilterValue] = useState("")
  const [emissionsData, setEmissionsData] = useState([])
  const [targetsData, setTargetsData] = useState([])
  const [reportingData, setReportingData] = useState([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [companyFilter, setCompanyFilter] = useState("")
  const [sectorFilter, setSectorFilter] = useState("")
  const [countryFilter, setCountryFilter] = useState("")
  const [yearFilter, setYearFilter] = useState("")
  const [competitorFilter, setCompetitorFilter] = useState([])

  const yesNoOptions = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ]

  useEffect(() => {
    fetchDatas()
    onPageLoadFilter()
  }, [formData])

  const isFilterApplied = Object.values(formData).some((value) => value)

  const fetchDatas = async () => {
    setLoading(true)
    try {
      // Prepare request body based on selected filters
      let requestBody = {};

      // Check if either companyName or competitors are selected
      const hasCompanyOrCompetitors = (formData.companyName && formData.companyName.length > 0) || 
                                    (formData.competitors && formData.competitors.length > 0);

      // If companyName or competitors are selected, year is required
      if (hasCompanyOrCompetitors) {
        requestBody.year = formData.year || 2024;
      }

      // Add primary_company if selected
      if (formData.companyName && formData.companyName.length > 0) {
        requestBody.primary_company = formData.companyName;
      }

      // Add competitors if selected
      if (formData.competitors && formData.competitors.length > 0) {
        requestBody.competitors = formData.competitors;
      }

      // If no filters are applied, send empty string
      if (Object.keys(requestBody).length === 0) {
        requestBody = "";
      }

      const apiRequests = [
        {
          url: `${process.env.REACT_APP_BASE_API_URL}csrd-benchmarking-dashboard`,
          body: requestBody,
          setData: setEmissionsData,
        },
      ]

      const responses = await Promise.all(
        apiRequests.map(({ url, body }) =>
          fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              username,
              companyid,
            },
            body: body === "" ? body : JSON.stringify(body),
          }),
        ),
      )

      await Promise.all(
        responses.map(async (res, index) => {
          const { setData } = apiRequests[index]
          if (res.status === 200) {
            const responseJson = await res.json()
            setData(responseJson)
            
            // Update chart data if available in response
            if (responseJson && responseJson.charts) {
              // Transform iro_visual_format data into array format
              const iroVisualData = responseJson.charts.iro_visual_format ? 
                Object.entries(responseJson.charts.iro_visual_format).map(([name, value]) => ({
                  name,
                  value,
                  color: getColorForIROFormat(name)
                })) : [];

              setChartData({
                external_assurance_percent: responseJson.charts.external_assurance_percent || 0,
                eu_taxonomy_percent: responseJson.charts.eu_taxonomy_percent || 0,
                companies_by_country: responseJson.charts.companies_by_country || [],
                emissions_profile: responseJson.charts.emissions_profile || [],
                workforce_and_board_diversity: responseJson.charts.workforce_and_board_diversity || [],
                iro_visual_format: iroVisualData,
                esrs_standards_mentioned: responseJson.tables?.esrs_standards_mentioned || [],
                framework_alignment: responseJson.tables?.framework_alignment || []
              });
            }
            
            // Check if KPIs exist in response and update state
            if (responseJson && responseJson.kpis) {
              setKpiData({
                company_count: responseJson.kpis.company_count || 0,
                average_csrd_page_count: responseJson.kpis.average_csrd_page_count || 0,
                average_material_topics_count: responseJson.kpis.average_material_topics_count || 0
              })
            }
          } else {
            console.error(`Error with API: ${apiRequests[index].url}`)
          }
        }),
      )
    } catch (error) {
      console.error("Error", error)
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 5000))
      setLoading(false)
    }
  }

  // Helper function to get color for IRO format
  const getColorForIROFormat = (format) => {
    const colorMap = {
      'Narrative': '#B4A34A',
      'Matrix': '#FAB430',
      'Matrix + Flowchart': '#EBEBEB',
      'Narrative + Diagram': '#3F7573',
      'None': '#999999'
    };
    return colorMap[format] || '#999999';
  };

  const onDropdownChange = (e, rowData, field) => {
    const updatedData = targetsData.map((row) => {
      if (row === rowData) {
        return { ...row, [field]: e.value }
      }
      return row
    })
    setTargetsData(updatedData)
  }

  const onInputChange = (e, rowData, field) => {
    const updatedData = targetsData.map((row) => {
      if (row === rowData) {
        return { ...row, [field]: e.target.value }
      }
      return row
    })
    setTargetsData(updatedData)
  }

  const netZeroTargetTemplate = (rowData) => {
    return (
      <Dropdown
        value={rowData.netZeroTarget}
        options={yesNoOptions}
        onChange={(e) => onDropdownChange(e, rowData, "netZeroTarget")}
        placeholder="Yes/No"
      />
    )
  }

  const nearTermTargetTemplate = (rowData) => {
    return (
      <InputNumber
        value={rowData.nearTermTarget}
        onValueChange={(e) => onInputChange(e, rowData, "nearTermTarget")}
        placeholder="Enter Value"
      />
    )
  }

  const targetStatementTemplate = (rowData) => {
    return (
      <InputText
        value={rowData.targetStatement}
        onChange={(e) => onInputChange(e, rowData, "targetStatement")}
        placeholder="Enter statement"
      />
    )
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { value: value, matchMode: FilterMatchMode.CONTAINS },
    }))
    setGlobalFilterValue(value)
  }

  const onTabChange = async (e) => {
    setActiveIndex(e.index)
    setFormData({
      companyName: [],
      sector: [],
      country: [],
      competitors: [],
      year: 2024,
    })
    setLoading(true)
    try {
      let url = ""
      let requestBody = {}
      
      url = `${process.env.REACT_APP_BASE_API_URL}csrd-benchmarking-dashboard-filters`
      requestBody = formData
      

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username,
        },
      })

      if (res.status === 200) {
        const responseJson = await res.json()
        console.log("Emission")
        setCompanyOptions(responseJson.companies_metadata.map((company) => ({ 
          label: company.company_name, 
          value: company.company_name,
        })))
        setSectorOptions(responseJson.sectors.map((name) => ({ label: name.label, value: name.label })))
        setCountryOptions(responseJson.countries.map((name) => ({ label: name.label, value: name.label })))
        setCompetitorsOptions(responseJson.companies_metadata.map((company) => ({ 
          label: company.company_name, 
          value: company.company_name,
        })))
        setYearOptions(responseJson.years.map((name) => ({ label: name.label, value: name.label })))
        setLoading(false)
      } else {
        console.error(`Error fetching data for tab index ${e.index}`)
      }
    } catch (error) {
      console.error("Error", error)
    } 
  }

  const onPageLoadFilter = async () => {
    const e = activeIndex
    const requestBody1 = {
      companyNames: formData.companyName?.length > 0 ? [formData.companyName] : [],
      sectors: formData.sector?.length > 0 ? formData.sector : [],
      countries: formData.country?.length > 0 ? formData.country : [],
      competitors: formData.competitors?.length > 0 ? formData.competitors : [],
      years: formData.year?.length > 0 ? [formData.year] : [],
    }
    console.log(formData)

    setLoading(true)
    try {
      let url = ""
      let requestBody = {}
      url = `${process.env.REACT_APP_BASE_API_URL}csrd-benchmarking-dashboard-filters`
      requestBody = requestBody1
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username,
        },
      })

      if (res.status === 200) {
        const responseJson = await res.json()
        console.log("Emission")
        setCompanyOptions(responseJson.companies_metadata.map((company) => ({ 
          label: company.company_name, 
          value: company.company_name,
        })))
        setSectorOptions(responseJson.sectors.map((name) => ({ label: name.label, value: name.label })))
        setCountryOptions(responseJson.countries.map((name) => ({ label: name.label, value: name.label })))
        setCompetitorsOptions(responseJson.companies_metadata.map((company) => ({ 
          label: company.company_name, 
          value: company.company_name,
        })))
        setYearOptions(responseJson.years.map((name) => ({ label: name.label, value: name.label })))
        setLoading(false);
      } else {
        console.error(`Error fetching data for tab index ${e.index}`)
      }
    } catch (error) {
      console.error("Error", error)
    } 
  }

  const renderHeader = () => {
    return (
      <div className="flex justify-content-end">
        <span className="p-input-icon-left">
          <img src="assets/img/search.png" className="search-i" alt="search" />
          <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
        </span>
      </div>
    )
  }

  const header = renderHeader()

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: "#00366D26",
      background: "#F5F5F5",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#407775",
      },
      fontSize: fontSize,
      color: "#707070!important",
      paddingLeft:
        state.selectProps.name === "companyName" && (!formData.companyName || formData.companyName.length === 0)
          ? "30px"
          : state.selectProps.name === "sector" && (!formData.sector || formData.sector.length === 0)
            ? "30px"
            : state.selectProps.name === "region" && (!formData.region || formData.region.length === 0)
              ? "30px"
              : state.selectProps.name === "country" && (!formData.country || formData.country.length === 0)
                ? "30px"
                : state.selectProps.name === "competitors" &&
                    (!formData.competitors || formData.competitors.length === 0)
                  ? "30px"
                  : state.selectProps.name === "year" && (!formData.year || formData.year.length === 0)
                    ? "30px"
                    : "0px",
      position: "relative",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#707070",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      "&:hover": {
        color: "#707070",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused || state.isSelected ? "#eef1f5" : "white",
      color: state.isSelected ? "#407775" : "#707070",
      fontSize: fontSize,
      "&:active": {
        backgroundColor: "#eef1f5",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: fontSize,
      color: "#707070",
    }),
  }

  const CSRDCharts = ({ 
    externalAssurancePercent, 
    euTaxonomyPercent,
    companiesByCountry,
    emissionsProfile,
    workforceAndBoardDiversity,
    iroVisualFormat,
    esrsStandardsMentioned,
    frameworkAlignment
  }) => {
    // External Assurance solid gauge chart
    const externalAssuranceOptions = {
      chart: {
        type: "solidgauge",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "Companies With External Assurance",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
        x: 10,
        y: 20,
      },
      pane: {
        center: ["50%", "70%"],
        size: "100%",
        startAngle: -90,
        endAngle: 90,
        background: {
          backgroundColor: "#E0E0E0",
          innerRadius: "60%",
          outerRadius: "100%",
          shape: "arc",
        },
      },
      tooltip: {
        enabled: false,
      },
      yAxis: {
        min: 0,
        max: 100,
        stops: [[1, "#6F9896"]],
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: null,
        tickAmount: 2,
        labels: {
          y: 16,
          style: {
            fontSize: "10px",
            color: "#666",
          },
        },
      },
      plotOptions: {
        solidgauge: {
          dataLabels: {
            y: -30,
            borderWidth: 0,
            useHTML: true,
            format:
              '<div style="text-align:center"><span style="font-size:24px;font-weight:bold;color:#494949">{y}%</span></div>',
          },
        },
      },
      series: [
        {
          name: "Assurance",
          data: [externalAssurancePercent || 0],
          dataLabels: {
            format:
              '<div style="text-align:center"><span style="font-size:24px;font-weight:bold;color:#494949">{y}%</span></div>',
          },
        },
      ],
      credits: { enabled: false },
    }

    // Country comparison bar chart
    const countryComparisonOptions = {
      chart: {
        type: "column",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "Count Of Companies By Country",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
      },
      xAxis: {
        categories: companiesByCountry?.map(item => item.country) || [],
        labels: {
          style: { fontSize: "10px" },
        },
      },
      yAxis: {
        min: 0,
        title: { text: "" },
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          name: "Companies",
          data: companiesByCountry?.map(item => item.count) || [],
          color: "#FAB430",
        },
      ],
      credits: { enabled: false },
    }

    // CSRD Title full circle donut chart with external label and simple arrow
    const csrdTitleOptions = {
      chart: {
        type: "pie",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "% Of Companies With Dedicated CSRD Title",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: false,
          },
          showInLegend: false,
        },
      },
      series: [
        {
          name: "CSRD Title",
          data: [{ name: "With Title", y: 100, color: "#6F9896" }],
        },
      ],
      tooltip: {
        enabled: false,
      },
      annotations: [
        {
          labels: [
            {
              point: {
                x: 30,
                y: 180,
              },
              text: "10 (100%)",
              useHTML: false,
              backgroundColor: "transparent",
              borderWidth: 0,
              shadow: false,
              style: {
                color: "#494949",
                fontSize: "11px",
                fontWeight: "normal",
              },
            },
          ],
          shapes: [
            {
              type: "path",
              points: [
                { x: 70, y: 185 },
                { x: 120, y: 150 },
              ],
              stroke: "#666",
              strokeWidth: 1,
              fill: "none",
            },
          ],
        },
      ],
      credits: { enabled: false },
    }

    // EU Taxonomy full circle donut chart with external label and simple arrow
    const euTaxonomyOptions = {
      chart: {
        type: "pie",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "Company With EU Taxonomy Section",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: false,
          },
          showInLegend: false,
        },
      },
      series: [
        {
          name: "EU Taxonomy",
          data: [{ name: "With Section", y: euTaxonomyPercent || 0, color: "#B4A34A" }],
        },
      ],
      tooltip: {
        enabled: false,
      },
      annotations: [
        {
          labels: [
            {
              point: {
                x: 30,
                y: 180,
              },
              text: `10 (${euTaxonomyPercent || 0}%)`,
              useHTML: false,
              backgroundColor: "transparent",
              borderWidth: 0,
              shadow: false,
              style: {
                color: "#494949",
                fontSize: "11px",
                fontWeight: "normal",
              },
            },
          ],
          shapes: [
            {
              type: "path",
              points: [
                { x: 70, y: 185 },
                { x: 120, y: 150 },
              ],
              stroke: "#666",
              strokeWidth: 1,
              fill: "none",
            },
          ],
        },
      ],
      credits: { enabled: false },
    }

    // Emissions Profile bar chart
    const emissionsProfileOptions = {
      chart: {
        type: "column",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "Emissions Profile",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
      },
      xAxis: {
        categories: emissionsProfile?.map(item => item.company) || [],
        labels: {
          style: { fontSize: "9px" },
        },
      },
      yAxis: {
        min: 0,
        title: { text: "Emissions", style: { fontSize: "10px" } },
      },
      legend: {
        itemStyle: {
          fontSize: "10px",
        },
      },
      series: [
        { 
          name: "Scope 1 emissions", 
          data: emissionsProfile?.map(item => item.scope1) || [], 
          color: "#6D9157" 
        },
        { 
          name: "Scope 2 emissions", 
          data: emissionsProfile?.map(item => item.scope2) || [], 
          color: "#B3A249" 
        },
        { 
          name: "Scope 3 emissions", 
          data: emissionsProfile?.map(item => item.scope3) || [], 
          color: "#FAB430" 
        },
      ],
      credits: { enabled: false },
    }

    // Calculate max value for yAxis
    const calculateMaxYAxis = (data) => {
      if (!data || data.length === 0) return 60; // Default max value
      
      const maxBoard = Math.max(...data.map(item => item.board_female_pct || 0));
      const maxWorkforce = Math.max(...data.map(item => item.workforce_female_pct || 0));
      const maxValue = Math.max(maxBoard, maxWorkforce);
      
      // Round up to nearest multiple of 10
      return Math.ceil(maxValue / 10) * 10;
    };

    // Workforce & Board Diversity
    const diversityOptions = {
      chart: {
        type: "column",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "Workforce & Board Diversity",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
      },
      xAxis: {
        categories: workforceAndBoardDiversity?.map(item => item.company) || [],
        labels: {
          style: { fontSize: "9px" },
        },
      },
      yAxis: {
        min: 0,
        max: calculateMaxYAxis(workforceAndBoardDiversity),
        title: { text: "Percentage", style: { fontSize: "10px" } },
      },
      legend: {
        itemStyle: {
          fontSize: "10px",
        },
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: "Board Diversity (Female)",
          data: workforceAndBoardDiversity?.map(item => item.board_female_pct) || [],
          color: "#9DB9B8",
          dataLabels: {
            enabled: true,
            style: {
              fontSize: "9px",
              fontWeight: "normal",
              color: "#494949",
              textOutline: "none",
            },
            format: "{y}",
            y: -5,
          },
        },
        {
          name: "Workforce Diversity (Female)",
          data: workforceAndBoardDiversity?.map(item => item.workforce_female_pct) || [],
          color: "#407775",
          dataLabels: {
            enabled: true,
            style: {
              fontSize: "9px",
              fontWeight: "normal",
              color: "#494949",
              textOutline: "none",
            },
            format: "{y}",
            y: -5,
          },
        },
      ],
      credits: { enabled: false },
    }

    // IRO Visual Format
    const iroVisualOptions = {
      chart: {
        type: "treemap",
        height: 250,
        backgroundColor: "transparent",
      },
      title: {
        text: "IRO Visual Format",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#494949",
        },
        align: "left",
      },
      colorAxis: {
        minColor: "#FFFFFF",
        maxColor: "#000000",
        visible: false,
      },
      series: [
        {
          type: "treemap",
          layoutAlgorithm: "squarified",
          allowDrillToNode: false,
          animationLimit: 1000,
          dataLabels: {
            enabled: true,
            style: {
              fontSize: "10px",
              fontWeight: "bold",
              color: "black",
              textOutline: "none",
            },
            format: "{point.name}<br/>{point.value}",
          },
          levelIsConstant: false,
          levels: [
            {
              level: 1,
              dataLabels: {
                enabled: true,
                style: {
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "black",
                  textOutline: "none",
                },
                format: "{point.name}<br/>{point.value}",
              },
              borderWidth: 2,
              borderColor: "#ffffff",
            },
          ],
          data: iroVisualFormat || [],
        },
      ],
      tooltip: {
        pointFormat:
          "<b>{point.name}</b><br/>Count: <b>{point.value}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>",
        style: {
          fontSize: "12px",
        },
      },
      credits: { enabled: false },
    }

    return (
      <>
        {/* First row - 4 charts */}
        <div className="row mb-3" style={{ gap: "15px 0" }}>
          <div className="col-md-3" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={externalAssuranceOptions} />
              </div>
            </div>
          </div>
          <div className="col-md-3" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={countryComparisonOptions} />
              </div>
            </div>
          </div>
          <div className="col-md-3" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={csrdTitleOptions} />
              </div>
            </div>
          </div>
          <div className="col-md-3" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={euTaxonomyOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Second row - 3 charts */}
        <div className="row mb-3" style={{ gap: "15px 0" }}>
          <div className="col-md-4" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={emissionsProfileOptions} />
              </div>
            </div>
          </div>
          <div className="col-md-4" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={diversityOptions} />
              </div>
            </div>
          </div>
          <div className="col-md-4" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <HighchartsReact highcharts={Highcharts} options={iroVisualOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Third row - 2 tables */}
        <div className="row" style={{ gap: "15px 0" }}>
          <div className="col-md-6" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <h6
                  style={{
                    color: "#494949",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginBottom: "15px",
                  }}
                >
                  ESRS Standards Mentioned
                </h6>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: "11px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#407775" }}>
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            color: "#EBF6F6",
                            fontWeight: "500",
                          }}
                        >
                          Company Name
                        </th>
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            color: "#EBF6F6",
                            fontWeight: "500",
                          }}
                        >
                          ESRS Standards Mentioned
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {esrsStandardsMentioned?.map((item, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#FCF7EE" : "#DAECEC" }}>
                          <td style={{ padding: "8px 12px", color: "#494949" }}>{item.company}</td>
                          <td style={{ padding: "8px 12px", color: "#494949" }}>{item.esrs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6" style={{ paddingLeft: "10px", paddingRight: "10px" }}>
            <div className="card" style={{ boxShadow: "0px 3px 6px #00000029", opacity: 1, border: "none" }}>
              <div className="card-body">
                <h6
                  style={{
                    color: "#494949",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginBottom: "15px",
                  }}
                >
                  Alignment With Other Frameworks
                </h6>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: "11px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#407775" }}>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap" }}>
                          Company Name
                        </th>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap" }}>
                          GRI Alignment
                        </th>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap" }}>
                          SBTi Target Mentioned
                        </th>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: "#EBF6F6", fontWeight: "500", whiteSpace: "nowrap" }}>
                          TCFD Referenced
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {frameworkAlignment?.map((item, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#FCF7EE" : "#DAECEC" }}>
                          <td style={{ padding: "8px 12px", color: "#494949" }}>{item.company}</td>
                          <td style={{ padding: "8px 12px", color: "#494949" }}>{item.gri_alignment ? "Yes" : "No"}</td>
                          <td style={{ padding: "8px 12px", color: "#494949" }}>{item.sbti_target_mentioned ? "Yes" : "No"}</td>
                          <td style={{ padding: "8px 12px", color: "#494949" }}>{item.tcfd_referenced ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div id="wrapper">
      {loading && (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div id="">
        <header>
          <nav className="navbar navbar-expand-lg navbar-light" style={{ background: "#407775" }}>
            <div className="container-fluid  mx-4 my-2">
              <div className="d-flex align-items-center">
                <a className="navbar-brand d-flex align-items-center p-0" href="#home">
                  <img src="assets/img/logo.png" alt="Company Logo" height="30" />
                </a>
                <div
                  className="mx-2"
                  style={{
                    borderLeft: "1px solid #fff",
                    height: "30px",
                    paddingLeft: "20px",
                    opacity: "0.5",
                  }}
                ></div>
                <span className="text-white" style={{ opacity: "0.7", fontSize: "14px" }}>
                  Organization Name
                </span>
              </div>
              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarSupportedContent"
                aria-controls="navbarSupportedContent"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
              <div className="collapse navbar-collapse justify-content-end" id="navbarSupportedContent">
                <ul className="navbar-nav mb-2 mb-lg-0 d-flex align-items-center">
                  <li className="nav-item">
                    <a className="nav-link  text-white" href="#notifications">
                      <MdNotificationsActive className="icon" style={{ width: "1.5rem", height: "1.5rem" }} />
                    </a>
                  </li>
                  <li className="nav-item">
                    <span className="text-white nav-link" style={{ opacity: "0.7", fontSize: "14px", cursor: "default" }}>
                      {username}
                    </span>
                  </li>
                  <li className="nav-item dropdown" style={{ position: "relative" }}>
                    <a className="nav-link text-white" href="#" onClick={(e) => { e.preventDefault(); setIsOpen(!isOpen) }}>
                      <img className="icon mr-2" src="assets/img/User.png" alt="User" />
                    </a>
                    {isOpen && (
                      <ul className="dropdown-menu show" style={{ position: "absolute", right: 0, top: "100%", zIndex: 9999, minWidth: "160px", overflow: "hidden", borderRadius: "4px" }}>
                        <li><button onMouseEnter={e => { e.currentTarget.style.background = "#407775"; e.currentTarget.style.color = "#fff"; }} onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#212529"; }} style={{ color: "#212529", background: "none", border: "none", width: "100%", textAlign: "left", padding: "6px 16px", cursor: "pointer" }} onClick={handleLogout}>Logout</button></li>
                      </ul>
                    )}
                  </li>
                  <li className="nav-item">
                    <span className="text-white" style={{ opacity: "0.7", fontSize: "14px" }}>
                      XYZ ltd
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        </header>

        <div className="container survey-form scf p-3 p-lg-4">
          <div className="row">
            <div className="col d-flex justify-content-between align-items-center">
              <div>
                <Link to="/esg-benchmarking" style={{ color: "#407775", fontSize: "14px", textDecoration: "none" }}>
                  Home
                </Link>
                <span style={{ color: "#707070", fontSize: "14px", margin: "0 8px" }}>{">"}</span>
                <span style={{ color: "#707070", fontSize: "14px" }}>CSRD Benchmarking</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="text"
                  style={{
                    color: "#407775",
                    fontSize: "14px",
                    textTransform: "none",
                    padding: "6px 16px",
                    border: "none",
                  }}
                  startIcon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                        stroke="#407775"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7 10L12 15L17 10"
                        stroke="#407775"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 15V3"
                        stroke="#407775"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                >
                  Download
                </Button>
              </div>
            </div>
          </div>

          <div className="row my-3">
            <div className="col">
              <h1 className="title" style={{ color: "#494949", marginBottom: "0",fontSize:"18px",fontWeight:"600" }}>
                CSRD Benchmarking
              </h1>
            </div>
          </div>
          <div className="form-part">
            <form>
              <div className="row gx-2 gy-3">
                <div className="col-xl-2 col-lg-4 col-md-6">
                  <label className="formlable">Company Name</label>
                  <div className="input-group position-relative">
                    {(!formData.companyName || formData.companyName.length === 0) && (
                      <span className="search-icon">
                        <img src="assets/img/search.png" alt="Search Icon" className="search-i" />
                      </span>
                    )}
                    <Select
                      name="companyName"
                      value={companyOptions.find((option) => option.value === formData.companyName) || null}
                      options={companyOptions}
                      onChange={(e) => setFormData({ ...formData, companyName: e ? e.value : null })}
                      placeholder="Search"
                      isSearchable
                      styles={customStyles}
                      className="custom-select-with-icon wd-100"
                    />
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6">
                  <label className="formlable">Sector</label>
                  <div className="input-group position-relative">
                    {(!formData.sector || formData.sector.length === 0) && (
                      <span className="search-icon">
                        <img src="assets/img/search.png" alt="Search Icon" className="search-i" />
                      </span>
                    )}
                    <Select
                      name="sector"
                      value={sectorOptions.filter((option) => formData.sector?.includes(option.value))}
                      options={sectorOptions}
                      onChange={(selectedOptions) => {
                        if (selectedOptions.length <= 5) {
                          setFormData({
                            ...formData,
                            sector: selectedOptions.map((opt) => opt.value),
                          })
                        }
                      }}
                      placeholder="Search"
                      isMulti
                      isSearchable
                      styles={customStyles}
                      className="custom-select-with-icon wd-100"
                      maxMenuHeight={200}
                    />
                  </div>
                </div>
                <div className="col-xl-2 col-lg-4 col-md-6">
                  <label className="formlable">Country</label>
                  <div className="input-group position-relative">
                    {(!formData.country || formData.country.length === 0) && (
                      <span className="search-icon">
                        <img src="assets/img/search.png" alt="Search Icon" className="search-i" />
                      </span>
                    )}
                    <Select
                      name="country"
                      value={countryOptions.filter((option) => formData.country?.includes(option.value))}
                      options={countryOptions}
                      onChange={(selectedOptions) => {
                        if (selectedOptions.length <= 5) {
                          setFormData({
                            ...formData,
                            country: selectedOptions.map((opt) => opt.value),
                          })
                        }
                      }}
                      placeholder="Search"
                      isMulti
                      isSearchable
                      styles={customStyles}
                      className="custom-select-with-icon wd-100"
                      maxMenuHeight={200}
                    />
                  </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6">
                  <label className="formlable">
                    Select Competitors <span style={{ fontSize: "10px" }}>(Upto 5)</span>
                  </label>
                  <div className="input-group position-relative">
                    {(!formData.competitors || formData.competitors.length === 0) && (
                      <span className="search-icon">
                        <img src="assets/img/search.png" alt="Search Icon" className="search-i" />
                      </span>
                    )}
                    <Select
                      name="competitors"
                      value={competitorsOptions.filter((option) => formData.competitors?.includes(option.value))}
                      options={competitorsOptions}
                      onChange={(selectedOptions) => {
                        console.log("Selected Competitors:", selectedOptions)
                        console.log("Form Data:", formData)
                        if (selectedOptions.length <= 5) {
                          setFormData({
                            ...formData,
                            competitors: selectedOptions.map((opt) => opt.value),
                          })
                        }
                      }}
                      placeholder="Search"
                      isMulti
                      isSearchable
                      styles={customStyles}
                      className="custom-select-with-icon wd-100"
                      maxMenuHeight={200}
                    />
                  </div>
                </div>
                {activeIndex !== 2 && (
                  <div className="col-xl-2 col-lg-4 col-md-6">
                    <label className="formlable">Year</label>
                    <div className="input-group position-relative">
                      {(!formData.year || formData.year.length === 0) && (
                        <span className="search-icon">
                          <img src="assets/img/search.png" alt="Search Icon" className="search-i" />
                        </span>
                      )}
                      <Select
                        name="year"
                        value={formData.year ? yearOptions.find((option) => option.value === formData.year) : null}
                        options={yearOptions}
                        onChange={(e) => setFormData({ ...formData, year: e.value })}
                        placeholder="Search"
                        isSearchable
                        styles={customStyles}
                        className="custom-select-with-icon wd-100"
                      />
                    </div>
                  </div>
                )}
                <div className="col-xl-2 col-lg-4 col-md-6">
                  <label className="formlable" style={{ visibility: "hidden" }}>
                    Reset
                  </label>
                  <Button
                    onClick={resetSelections}
                    variant="outlined"
                    style={{
                      width: "100%",
                      height: "38px",
                      color: "#407775",
                      borderColor: "#407775",
                      fontSize: "14px",
                      textTransform: "none",
                      backgroundColor: "transparent",
                    }}
                    startIcon={<MdRefresh />}
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>

              <div className="row mt-2 mb-3">
                <div className="col-md-2">
                  <div
                    style={{
                      border: "1px solid #E5E5E5",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      padding: "20px 16px",
                      textAlign: "center",
                      height: "100px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#707070",
                        fontWeight: "400",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Number of companies
                    </p>
                    <h3
                      style={{
                        color: "#494949",
                        fontSize: "32px",
                        fontWeight: "bold",
                        margin: "0",
                        lineHeight: "1",
                      }}
                    >
                      {kpiData.company_count}
                    </h3>
                  </div>
                </div>
                <div className="col-md-2">
                  <div
                    style={{
                      border: "1px solid #E5E5E5",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      padding: "20px 16px",
                      textAlign: "center",
                      height: "100px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#707070",
                        fontWeight: "400",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Average CSRD page count
                    </p>
                    <h3
                      style={{
                        color: "#494949",
                        fontSize: "32px",
                        fontWeight: "bold",
                        margin: "0",
                        lineHeight: "1",
                      }}
                    >
                      {kpiData.average_csrd_page_count.toFixed(1)}
                    </h3>
                  </div>
                </div>
                <div className="col-md-2">
                  <div
                    style={{
                      border: "1px solid #E5E5E5",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      padding: "20px 16px",
                      textAlign: "center",
                      height: "100px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#707070",
                        fontWeight: "400",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Average material topics count
                    </p>
                    <h3
                      style={{
                        color: "#494949",
                        fontSize: "32px",
                        fontWeight: "bold",
                        margin: "0",
                        lineHeight: "1",
                      }}
                    >
                      {kpiData.average_material_topics_count.toFixed(1)}
                    </h3>
                  </div>
                </div>
              </div>

              <TabView activeIndex={activeIndex} onTabChange={onTabChange}>
                <TabPanel
                  header={
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <img src="assets/img/summary.svg" alt="Summary" width="16" height="16" />
                      <span style={{ fontSize: "12px" }}>Summary</span>
                    </div>
                  }
                >
                  <div className="row tabs mt-3">
                    <CSRDCharts 
                      externalAssurancePercent={chartData.external_assurance_percent}
                      euTaxonomyPercent={chartData.eu_taxonomy_percent}
                      companiesByCountry={chartData.companies_by_country}
                      emissionsProfile={chartData.emissions_profile}
                      workforceAndBoardDiversity={chartData.workforce_and_board_diversity}
                      iroVisualFormat={chartData.iro_visual_format}
                      esrsStandardsMentioned={chartData.esrs_standards_mentioned}
                      frameworkAlignment={chartData.framework_alignment}
                    />
                  </div>
                </TabPanel>
                <TabPanel
                  header={
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <img src="assets/img/deepdive.svg" alt="Deep Dive" width="16" height="16" />
                      <span style={{ fontSize: "12px" }}>Deep Dive</span>
                    </div>
                  }
                >
                  <div className="row tabs mt-3">
                    <div
                      className="col-12 d-flex justify-content-center align-items-center"
                      style={{ minHeight: "400px" }}
                    >
                      <Link to="/deep-dive" style={{ textDecoration: 'none' }}>
                        <Button variant="contained" color="primary" style={{ backgroundColor: "#407775", color: "white" }}>
                          Go to Deep Dive Page
                        </Button>
                      </Link>
                    </div>
                  </div>
                </TabPanel>
              </TabView>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ESGBenchmarking
